<script setup lang="ts">
import type { BaseButtonProps } from "../base/types";

type Props = BaseButtonProps & {
  theme?: Theme;
  disabled?: boolean;
};

const props = defineProps<Props>();
const attrs = useAttrs();

const themeSegments = computed<string[]>(() => (props.theme?.length ? props.theme?.split("-") : []));

const themeColor = computed(() => {
  return themeSegments.value.length ? themeColors.find((item) => item === themeSegments.value[0]) : undefined;
});

const themeType = computed(() => {
  return themeSegments.value.length
    ? themeTypes.find((item) => item === themeSegments.value[0] || item === themeSegments.value[1])
    : undefined;
});

const themeSize = computed(() => {
  return themeSegments.value.length
    ? themeSizes.find((item) => themeSegments.value.some((segment) => segment === item))
    : undefined;
});

const classes = computed<string[]>(() => {
  if (themeSegments.value.length > 3) {
    devConsole.warn(`Theme '${props.theme}' is invalid`);
  }

  if (
    ![undefined, ...themeColors].includes(themeColor.value) ||
    ![undefined, ...themeTypes].includes(themeType.value) ||
    ![undefined, ...themeSizes].includes(themeSize.value)
  ) {
    devConsole.warn(`Theme '${props.theme}' is invalid`);
  }

  const expectedTheme = [themeColor.value, themeType.value, themeSize.value].filter(Boolean).join("-");

  if (props.theme && expectedTheme !== props.theme) {
    devConsole.warn(`Theme '${props.theme}' is invalid`);
  }

  // Default values
  const color = themeColor.value ?? "primary";
  const type = themeType.value ?? "filled";
  const size = themeSize.value ?? "medium";
  const disabled = props.disabled ? "theme-disabled" : "";

  return ["button-theme", `theme-color-${color}`, `theme-type-${type}`, `theme-size-${size}`, disabled];
});
</script>
<template>
  <base-button v-bind="$attrs" :type :inactive :disabled="disabled || undefined" :class="classes">
    <slot />
  </base-button>
</template>

<style scoped lang="postcss">
/* Base */
.button-theme {
  @apply inline-flex items-center rounded-full border shadow-sm gap-2;
  @apply transition-all duration-200 justify-center;

  &.theme-disabled {
    @apply opacity-50 grayscale pointer-events-none;
  }
}

/* Colors */
.theme-color- {
  &primary {
    @apply text-ngray-8 bg-nblue-1;

    &.theme-hover,
    &:hover {
      @apply text-nblue-1 bg-nblue-3;
    }
    &.theme-type-outline,
    &.theme-type-clear {
      @apply text-nblue-1 border-nblue-1;

      &.theme-hover,
      &:hover {
        @apply text-nblue-2 bg-nblue-4;
      }
    }
  }
  &secondary {
    @apply text-nblue-6 bg-nblue-1;

    &.theme-hover,
    &:hover {
      @apply text-nblue-1 bg-nblue-6 border border-nblue-1;
    }
    &.theme-type-outline,
    &.theme-type-clear {
      @apply text-nblue-5 border-nblue-5;

      &.theme-hover,
      &:hover {
        @apply text-nblue-6 bg-nblue-1;
      }
    }
  }

  &gray {
    @apply text-black bg-ngray-6;

    &.theme-hover,
    &:hover {
      @apply bg-ngray-5;
    }

    &.theme-type-outline,
    &.theme-type-clear {
      @apply text-ngray-3 border-ngray-3;

      &.theme-hover,
      &:hover {
        @apply text-ngray-3 bg-ngray-6;
      }
    }
  }
  &tertiary {
    @apply text-ngray-1;

    &.theme-hover,
    &:hover {
      @apply text-ngray-3 border border-ngray-1;
    }

    &.theme-type-outline,
    &.theme-type-clear {
      @apply text-ngray-1 border-ngray-1;

      &.theme-hover,
      &:hover {
        @apply text-ngray-3 border-ngray-3;
      }
    }
  }
  &danger {
    @apply text-ngray-8 bg-nred-5;

    &.theme-hover,
    &:hover {
      @apply bg-nred-1;
    }
    &.theme-type-outline,
    &.theme-type-clear {
      @apply text-nred-5 border-nred-5;

      &.theme-hover,
      &:hover {
        @apply text-nred-5 bg-nred-4;
      }
    }
  }
  &success {
    @apply text-ngray-8 bg-ngreen-1;

    &.theme-hover,
    &:hover {
      @apply text-ngreen-6 bg-ngreen-5;
    }
    &.theme-type-outline,
    &.theme-type-clear {
      @apply text-ngreen-6 border-ngreen-6;

      &.theme-hover,
      &:hover {
        @apply text-ngreen-6 bg-ngreen-5;
      }
    }
  }
  &warning {
    @apply text-ngray-8 bg-nyellow-6;

    &.theme-hover,
    &:hover {
      @apply bg-nyellow-1 text-nyellow-5;
    }
    &.theme-type-outline,
    &.theme-type-clear {
      @apply text-nyellow-6 border-nyellow-6;

      &.theme-hover,
      &:hover {
        @apply text-nyellow-6 bg-nyellow-4;
      }
    }
  }
}

/* Types */
.theme-type- {
  &filled {
    @apply border border-transparent;
  }
  &outline {
    @apply border bg-ngray-8;
  }
  &clear {
    @apply border-none shadow-none bg-transparent;
  }
  &card {
    @apply w-full h-[265px] flex-col flex-1 justify-center rounded-lg /*hover:*/!shadow-lg;
  }
}

/* Sizes */
.theme-size- {
  &fit {
    @apply text-sm font-normal;
  }
  &thin {
    @apply text-sm font-normal py-1 px-4;
  }
  &medium {
    @apply text-base font-semibold py-2 px-4;
  }
  &huge {
    @apply text-3xl px-6 py-10 gap-4;
  }
}
</style>
